import { LogoutOutlined, QuestionCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Menu } from 'antd';
import type { MenuClickEventHandler } from 'rc-menu/lib/interface';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { CustomRouteProps } from '../../../pages/routes';
import { config } from '../../../utils';

import styles from './HeaderMenu.module.less';

interface Props {
  mode: 'inline' | 'horizontal';
  items: Array<CustomRouteProps>;
  onLogout?: () => void;
  onClick?: MenuClickEventHandler;
}

export const HeaderMenu = ({ mode, items, onLogout, onClick }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();

  const selectedKeys = React.useMemo(() => {
    let locationMatchPath: string | null = null;

    for (let item of items) {
      const locationMatch = matchPath(location.pathname, { path: item.path });

      // All paths will inexactly match '/' (except '/' itself which will match exactly)
      // so only accpet it if it's an exact match
      if (locationMatch && (locationMatch.isExact || item.path !== '/')) {
        locationMatchPath = item.path;
      }
    }

    return locationMatchPath ? [locationMatchPath] : [];
  }, [items, location.pathname]);

  return (
    <Menu className={styles.headerMenu} mode={mode} selectedKeys={selectedKeys} onClick={onClick}>
      {items.map(({ path, label, icon }) => (
        <Menu.Item key={path} icon={icon}>
          <Link to={path}>{label && t(label)}</Link>
        </Menu.Item>
      ))}

      {config.HOWTO_LINK ? (
        <Menu.Item key="howto" icon={<QuestionCircleOutlined />}>
          <a href={config.HOWTO_LINK} target="_blank" rel="noopener noreferrer">
            {t('HOWTO')}
          </a>
        </Menu.Item>
      ) : null}

      <Menu.SubMenu className={styles.userMenu} title={<Avatar size="small" icon={<UserOutlined />} />}>
        <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={onLogout}>
          {t('LOGOUT_BUTTON')}
        </Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );
};
